import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BASE_PATH, LOGIN_PATH } from 'router/pathName';
import { ConfirmDialog, primaryButton } from 'components/Operator/OperatorUi';
import { enterSupport, supportNavigation } from './supportApi';
import { useEnterErrors } from './supportUi';
import SupportAccessPanel from './SupportAccessPanel';

/**
 * YAC-2026-009 PR7b。運営の画面の会社の詳細に置く「運営のサポート」。「この会社の管理者として入る」と、その会社の
 * サポートの記録（今効いているもの。終わったものも表示できる）と「終わらせる」。
 *
 * 入る前の確認に、期限（作ってから24時間。運営のログインの期限が先ならそこまで）・操作が運営の名前で記録に残ること・
 * お客様の画面に見えることを書く（PR7a の申し送り2）。入ったら、管理者の画面を開き直す（帯つき）。
 */
const CompanySupport: React.FC<{ companyId: number; companyName: string }> = ({ companyId, companyName }) => {
  const { t } = useTranslation('layout');
  const errorsOf = useEnterErrors();
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [signIn, setSignIn] = useState(false);

  const enter = async () => {
    if (busy) return;
    setBusy(true);
    setErrors([]);
    try {
      await enterSupport(companyId);
      supportNavigation.assign(BASE_PATH);
      return;
    } catch (failure) {
      console.error('Failed to enter support', failure);
      const result = errorsOf(failure);
      setErrors(result.messages);
      setSignIn(result.signIn);
    }
    setBusy(false);
  };

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4 rounded-md bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 id={titleId} className="text-subtitle-1 font-bold text-neutral/300">
          {t('support.section-title')}
        </h2>
        <button
          type="button"
          className={primaryButton}
          onClick={() => {
            setErrors([]);
            setSignIn(false);
            setConfirming(true);
          }}
        >
          {t('support.enter')}
        </button>
      </div>
      <p className="text-body-2 text-neutral/300">{t('support.section-lead')}</p>
      <SupportAccessPanel company={companyId} labelledBy={titleId} />
      {confirming ? (
        <ConfirmDialog
          title={t('support.enter-title')}
          body={
            <>
              <p className="font-medium">{t('support.enter-confirm-company', { company: companyName })}</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>{t('support.enter-confirm-expiry')}</li>
                <li>{t('support.enter-confirm-audit')}</li>
                <li>{t('support.enter-confirm-visible')}</li>
              </ul>
              {signIn ? (
                <a href={LOGIN_PATH} className="font-medium text-primary/300 underline">
                  {t('support.sign-in')}
                </a>
              ) : null}
            </>
          }
          confirmLabel={busy ? t('support.entering') : t('support.enter-button')}
          busy={busy}
          errors={errors}
          onClose={() => setConfirming(false)}
          onConfirm={enter}
        />
      ) : null}
    </section>
  );
};

export default CompanySupport;
